// utils/customerPaymentPosting.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- SHARED, production POST
// /ar-ap/customer-payments body type + the exact fee-resolution builder that
// SaleDetail itself calls (Condition 4b / Finding 1 & 3). This endpoint is
// always fee-relevant (handleMarkPaid runs only for a positive payment), so the
// body always carries a fee_mode.

import { buildFeeIntent, FeeIntent } from "./feeIntent";
import { parseFeeOverride } from "./feeOverride";

// The ACTUAL posted object, TYPED. Fee portion is the strict discriminated
// FeeIntent (configured forbids fee via `fee?: never`; explicit requires a
// numeric fee), so configured-plus-fee and explicit-without-fee cannot compile.
export type CustomerPaymentPostBody = {
  company_id: string;
  invoice_id: number;
  payment_date: string;
  amount: number;
  payment_account_id: number;
  payment_method_id: number;
} & FeeIntent;

export type CustomerPaymentFeeResolution =
  | { ok: true; feeMode: "configured" }
  | { ok: true; feeMode: "explicit"; fee: number }
  | { ok: false; reason: "invalid-fee" };

/**
 * Resolve SaleDetail's posting fee intent. The SAME function the page calls.
 *
 * - cash: no override control -> explicit 0;
 * - override shown + valid non-blank number (incl 0): explicit;
 * - override hidden or blank: configured (backend computes; a stale hidden
 *   value is never submitted);
 * - invalid non-blank override -> { ok:false } so the caller blocks BEFORE any
 *   network call (never serializes NaN). Uses Number() (parseFeeOverride).
 */
export function resolveCustomerPaymentFee(input: {
  isCash: boolean;
  overrideShown: boolean;
  overrideRaw: string;
}): CustomerPaymentFeeResolution {
  const overrideProvided =
    !input.isCash && input.overrideShown && input.overrideRaw.trim() !== "";
  let feeValue: number | null = null;
  if (overrideProvided) {
    const parsed = parseFeeOverride(input.overrideRaw);
    if (!parsed.ok) return { ok: false, reason: "invalid-fee" };
    feeValue = parsed.value;
  }
  const intent = buildFeeIntent({
    hasPositiveOfflinePayment: true, // this endpoint is always a positive payment
    isCash: input.isCash,
    explicit: overrideProvided,
    feeValue,
  });
  if (intent && intent.fee_mode === "explicit") {
    return { ok: true, feeMode: "explicit", fee: intent.fee };
  }
  return { ok: true, feeMode: "configured" };
}

/** Strict FeeIntent request fields for the typed body / canonical identity. */
export function customerPaymentFeeIntentFields(
  r: { feeMode: "configured" } | { feeMode: "explicit"; fee: number },
): FeeIntent {
  return r.feeMode === "explicit"
    ? { fee_mode: "explicit", fee: r.fee }
    : { fee_mode: "configured" };
}
